import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import taskDrawerService from './taskDrawer.service';
import taskManagerService from '../ImprovedTaskManager/taskManager.service';
import { setUsers, getUserList } from '../../../../redux/slices/userSlice';

/**
 * Custom hook for task drawer logic
 *
 * actionTask will set when user clicks on the any task from action items in summary
 * without that data will be set taken from selectedTask
 *
 * if actionTask exist and user is clicking on back button then first we will reset the actionTask
 * it will by default creates the navigation
 *
 * Parent
 *      TaskDrawer
 */
export const useTaskDrawer = ({
  selectedTask,
  isDrawerOpen,
  onClose,
  modifyTasks,
  fetchTasks,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [taskDetails, setTaskDetails] = useState(selectedTask);
  const [actionTask, setActionTask] = useState(null);

  const bodyRef = useRef(null);
  const existingUsers = useSelector(getUserList);

  // Fetch users when drawer opens to populate Redux store
  // Only fetch if users are not already loaded to avoid unnecessary API calls
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await taskManagerService.fetchUsers();
        dispatch(setUsers(users));
      } catch (error) {
        console.error('Error fetching users for task drawer:', error);
      }
    };

    if (isDrawerOpen && (existingUsers?.length === 0)) {
      fetchUsers();
    }
  }, [isDrawerOpen, dispatch, existingUsers]);

  // memorize object before setting it to state for better performance
  const memorizedSelectedTask = useMemo(() => {
    return selectedTask;
  }, [selectedTask]);

  const memorizedTaskDetails = useMemo(() => {
    return actionTask || taskDetails;
  }, [actionTask, taskDetails]);

  const isShowingActionItem = useMemo(() => {
    return !!actionTask;
  }, [actionTask]);

  // set the task details when selectedTask changes
  useEffect(() => {
    setTaskDetails(memorizedSelectedTask);
  }, [memorizedSelectedTask]);

  const fetchTaskDetails = useCallback(async () => {
    try {
      setLoading(true);
      const details = await taskManagerService.getTaskDetails(
        taskDetails?.id,
        taskDetails?.version
      );
      if (isShowingActionItem) {
        setActionTask(null);
      }
      setTaskDetails(prev => ({ ...prev, ...details }));
    } catch (error) {
      console.error('Error fetching task details:', error);
    } finally {
      setLoading(false);
    }
  }, [taskDetails?.id, taskDetails?.version, isShowingActionItem]);

  // Handle drawer close
  const handleClose = useCallback(() => {
    if (isShowingActionItem) {
      setActionTask(null);
    }
    onClose();
  }, [onClose, isShowingActionItem]);

  const onBackPress = useCallback(
    e => {
      e?.preventDefault();
      bodyRef.current?.scrollTo({ top: 0 });
      setActionTask(null);
    },
    [bodyRef]
  );

  const onActionItemClick = useCallback(
    task => {
      bodyRef.current?.scrollTo({ top: 0 });
      setActionTask(task);
    },
    [bodyRef]
  );

  const handleFeedbackSubmit = useCallback(
    feedbackSentiment => {
      return async e => {
        e?.preventDefault();
        try {
          await taskDrawerService.setFeedbackValue({
            comments: '',
            feedbackSentiment,
            taskId: taskDetails?.id,
            taskTitle: taskDetails?.task_title || taskDetails?.taskTitle || '',
            taskStatus: taskDetails?.status || '',
          });
          await fetchTaskDetails();
        } catch (error) {
          console.error('Error setting feedback value:', error);
        }
      };
    },
    [taskDetails, fetchTaskDetails]
  );

  const handleAssignUserChange = useCallback(
    async userId => {
      try {
        await taskDrawerService.changeTaskOwner(
          memorizedTaskDetails?.id,
          userId
        );
        fetchTaskDetails();
        fetchTasks();
      } catch (error) {
        console.error('Error changing task owner:', error);
      }
    },
    [memorizedTaskDetails?.id, fetchTaskDetails]
  );

  return {
    // State
    loading,
    bodyRef,
    taskDetails: memorizedTaskDetails,
    showBackButton: !!actionTask,

    // Functions
    handleAssignUserChange,
    onActionItemClick,
    onBackPress,
    fetchTaskDetails,
    handleClose,
    handleFeedbackSubmit,
  };
};

export default useTaskDrawer;
